import '@/assets/styles/components/meet-appointment-toast.styles.scss';
import { Button } from '@/components';
import { useMeeting } from '@/context/meeting-context';
import { useAccurateCountdown } from '@/utility/use-util.tsx';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useApi } from '@/api/useApi';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useServerTimeStore } from '@/zustand/server-time/store.ts';
import { parseJwt } from '@/utility';
import { useAuthStore } from '@/zustand/auth/store.ts';
import { useDevicePermissions } from '@/context/device-permission-context.tsx';

type Appointment = {
	appointmentId: number;
	appointmentStartDate: string;
	appointmentEndDate: string;
	appointmentStatus: string;
	studentName: string;
	studentSurname: string;
	studentGradeName: string;
	avatarUrl: string;
	interactionId: string;
};

type UserInformationProps = {
	appointment: Appointment | null;
};

type ActionButtonProps = {
	appointment: Appointment | null;
	onStartCall: () => void;
};

const UserInformation = ({ appointment }: UserInformationProps) => {
	if (!appointment) return null;

	const getFullName = () => {
		const fullName = appointment.studentName + appointment.studentSurname;

		if (fullName?.length > 15) {
			return appointment.studentName + ' ' + appointment.studentSurname?.substring(0, 1) + '.';
		}

		return appointment.studentName + ' ' + appointment.studentSurname;
	};

	return (
		<div className="meet-appointment-toast-content-user-information">
			<span className="meet-appointment-toast-content-user-information-user-label">{getFullName()}</span>
			<span className="meet-appointment-toast-content-user-information-user-state">{appointment.studentGradeName || ''}</span>
		</div>
	);
};

const ActionButton = ({ appointment, onStartCall }: ActionButtonProps) => {
	const countdown = useAccurateCountdown();
	const { controlPermission } = useDevicePermissions();

	const handleStartCall = () => {
		if (countdown.isCompleted) {
			onStartCall();
		}
	};

	useEffect(() => {
		if (!appointment) return;

		const appointmentTime = new Date(appointment.appointmentStartDate);
		const now = new Date();

		const timeToAppointment = appointmentTime.getTime() - now.getTime();

		if (timeToAppointment >= 0 && timeToAppointment <= 300000) {
			countdown.reset(appointmentTime);
		} else if (timeToAppointment <= 0) {
			countdown.reset(new Date(now.getTime() - 1000));
		}
	}, [appointment]);

	return (
		<div className="meet-appointment-toast-content-user-information-button">
			<Button
				size="medium"
				variant="success"
				onClick={() => controlPermission(handleStartCall)}>
				{countdown.isCompleted ? (
					<span>Bağlan</span>
				) : (
					<>
						<b>{countdown.formattedTime}</b>
						<span style={{ fontWeight: 500 }}> sn</span>
					</>
				)}
			</Button>
		</div>
	);
};

const MeetAppointmentToast = forwardRef(function MeetAppointmentToast(
	{
		isVisible,
		appointment
	}: {
		isVisible: boolean;
		appointment: Appointment | null;
	},
	ref
) {
	const [shouldShow, setShouldShow] = useState(false);
	const { onNavigateAppointmentMeetRoom } = useMeeting();
	const serverTimeRef = useRef<Date | null>(null);
	const { data: serverTimeData } = useServerTimeStore();
	const { data: authData } = useAuthStore();
	const { MeetingControllerApi, ExpertControllerApi } = useApi();

	useEffect(() => {
		serverTimeRef.current = serverTimeData?.serverTime || new Date();
	}, [serverTimeData]);

	const checkAppointmentTime = () => {
		const appointmentTime = new Date(appointment.appointmentStartDate);
		const now = new Date(serverTimeRef.current);

		return appointmentTime.getTime() - now.getTime();
	};

	useEffect(() => {
		if (isVisible && appointment) {
			const timeToAppointment = checkAppointmentTime();

			if ((timeToAppointment >= 0 && timeToAppointment <= 300000) || (timeToAppointment <= 0 && appointment)) {
				setShouldShow(true);
			} else {
				setShouldShow(false);
			}
		} else {
			setShouldShow(false);
		}
	}, [isVisible, appointment]);

	const isApplicant = useMemo(() => {
		if (authData?.access_token) {
			const resourceAccess = parseJwt(authData?.access_token)?.resource_access;
			return resourceAccess?.['live-expert-applicant']?.roles?.includes('Expert_Applicant');
		}
		return false;
	}, [authData?.access_token]);

	const { data: expertDetailInfo } = useQueryProvider([QUERY_KEYS.getLoggedInExpertDetailInfo], () => ExpertControllerApi.getLoggedInExpertDetailInfo(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		enabled: !isApplicant && !!authData?.access_token
	});

	const handleStartCall = async () => {
		if (appointment && appointment.interactionId) {
			try {
				const response = await MeetingControllerApi.getMeetingRoomInformation(appointment.interactionId);
				const meetingData = response?.data?.data;

				if (meetingData) {
					const appointmentMeetRoomData = {
						roomId: meetingData?.roomId,
						providerParticipantId: meetingData?.providerParticipantId,
						videoCallProviderInfo: {
							host: meetingData?.videoCallProviderInfo?.host,
							name: meetingData?.videoCallProviderInfo?.name
						},
						token: meetingData?.token,
						meetingId: meetingData?.meetingId,
						endDate: meetingData?.endDate,
						meetingLastConnectionDate: meetingData?.meetingLastConnectionDate,
						extensionNotificationDate: meetingData?.extensionNotificationDate,
						studentName: `${appointment?.studentName} ${appointment?.studentSurname}`,
						studentAvatar: appointment?.avatarUrl || null,
						coachName: expertDetailInfo ? `${expertDetailInfo?.expert?.name} ${expertDetailInfo?.expert?.surname}` : '',
						coachAvatar: expertDetailInfo?.expert?.profilePicture,
						coachId: expertDetailInfo?.expert?.id,
						interactionId: appointment?.interactionId,
						liveServiceGroup: meetingData?.liveServiceGroup
					};

					onNavigateAppointmentMeetRoom(appointmentMeetRoomData);
				}
			} catch (error) {
				console.error('Error getting meeting information:', error);
			}
		}
	};

	useImperativeHandle(ref, () => ({
		startCall: handleStartCall
	}));

	if (!shouldShow) {
		return null;
	}

	return (
		<div className="meet-appointment-toast-container">
			<div className={`meet-appointment-toast ${shouldShow ? 'visible' : 'hidden'}`}>
				<div className="meet-appointment-toast-content">
					<div className="meet-appointment-toast-content-avatar">
						{appointment?.avatarUrl ? (
							<img
								src={appointment.avatarUrl}
								alt={appointment.studentName}
							/>
						) : (
							<i className="icons-user-circle" />
						)}
					</div>
					<UserInformation appointment={appointment} />
					<ActionButton
						appointment={appointment}
						onStartCall={handleStartCall}
					/>
				</div>
			</div>
		</div>
	);
});

export default MeetAppointmentToast;
